import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Flame, RefreshCw, Plus } from "lucide-react";
import { toast } from "react-toastify";
import { SpinLoader } from "../../../components/loader/Loader";
import {
  acknowledgeMotionAlert,
  createMotionAlert,
  getAllCameras,
  getMotionAlerts,
} from "../../../service/securityService";
import type { Camera, MotionAlert } from "../../../types/security_types";

const MotionAlertsScreen: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState<MotionAlert[]>([]);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [onlyOpen, setOnlyOpen] = useState(true);
  const [simCameraId, setSimCameraId] = useState("");
  const [simSubmitting, setSimSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [list, cams] = await Promise.all([
        getMotionAlerts({ limit: 150, onlyOpen }),
        getAllCameras({ limit: 200 }),
      ]);
      setAlerts(list);
      setCameras(cams);
      setSimCameraId((prev) => prev || cams[0]?.cameraId || "");
    } catch (e) {
      console.error(e);
      toast.error("Failed to load motion alerts");
    } finally {
      setLoading(false);
    }
  }, [onlyOpen]);

  useEffect(() => {
    load();
  }, [load]);

  const ack = async (a: MotionAlert) => {
    const id = a.id || a._id;
    if (!id) return;
    try {
      await acknowledgeMotionAlert(id);
      toast.success("Alert acknowledged");
      await load();
    } catch (e) {
      console.error(e);
      toast.error("Could not acknowledge");
    }
  };

  const simulate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!simCameraId.trim()) {
      toast.error("Choose a camera");
      return;
    }
    setSimSubmitting(true);
    try {
      const cam = cameras.find((c) => c.cameraId === simCameraId.trim());
      await createMotionAlert({
        cameraId: simCameraId.trim(),
        cameraName: cam?.cameraName,
        severity: "Medium",
        message: "Motion detected (simulated / test event)",
      });
      toast.success("Motion alert created");
      await load();
    } catch (err) {
      console.error(err);
      toast.error("Failed to create alert");
    } finally {
      setSimSubmitting(false);
    }
  };

  return (
    <div className="px-2 py-4 mt-14 min-h-screen space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/security/surveillance")}
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4" />
            Overview
          </button>
          <div className="bg-[#EAFBFA] p-3 rounded-lg">
            <Flame className="w-6 h-6 text-[#0fa9a6]" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Motion detection alerts</h2>
            <p className="text-sm text-gray-600">
              VMS and analytics can POST alerts to the surveillance API; use the form below for
              demos.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={onlyOpen}
              onChange={(e) => setOnlyOpen(e.target.checked)}
            />
            Open only
          </label>
          <button
            type="button"
            onClick={() => load()}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-full text-sm hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      <form
        onSubmit={simulate}
        className="bg-white border border-gray-200 rounded-lg p-4 flex flex-wrap gap-3 items-end shadow-sm"
      >
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600">Camera (test event)</label>
          <select
            value={simCameraId}
            onChange={(e) => setSimCameraId(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm min-w-[220px]"
          >
            {cameras.length === 0 && <option value="">No cameras</option>}
            {cameras.map((c) => (
              <option key={c.cameraId} value={c.cameraId}>
                {c.cameraName} ({c.cameraId})
              </option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          disabled={simSubmitting || cameras.length === 0}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0fa9a6] text-white text-sm font-semibold hover:bg-teal-600 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
          Simulate motion
        </button>
      </form>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
        {loading ? (
          <div className="flex justify-center py-16">
            <SpinLoader />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#E9F5FF] text-left">
                <tr>
                  <th className="p-3 font-semibold">When</th>
                  <th className="p-3 font-semibold">Camera</th>
                  <th className="p-3 font-semibold">Severity</th>
                  <th className="p-3 font-semibold">Message</th>
                  <th className="p-3 font-semibold">Status</th>
                  <th className="p-3 font-semibold w-36" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {alerts.map((a) => {
                  const id = a.id || a._id;
                  return (
                    <tr key={id || `${a.cameraId}-${a.detectedAt}`}>
                      <td className="p-3 text-gray-600 whitespace-nowrap">
                        {new Date(a.detectedAt).toLocaleString()}
                      </td>
                      <td className="p-3">
                        <div className="font-medium">{a.cameraId}</div>
                        {a.cameraName && (
                          <div className="text-xs text-gray-500">{a.cameraName}</div>
                        )}
                      </td>
                      <td className="p-3">{a.severity || "—"}</td>
                      <td className="p-3 max-w-xs truncate" title={a.message}>
                        {a.message}
                      </td>
                      <td className="p-3">
                        {a.acknowledged ? (
                          <span className="text-emerald-700 text-xs font-medium">Acknowledged</span>
                        ) : (
                          <span className="text-amber-700 text-xs font-medium">Open</span>
                        )}
                      </td>
                      <td className="p-3">
                        {!a.acknowledged && id && (
                          <button
                            type="button"
                            onClick={() => ack(a)}
                            className="text-xs font-semibold text-[#0fa9a6] hover:underline"
                          >
                            Acknowledge
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {alerts.length === 0 && (
              <p className="p-8 text-center text-gray-500 text-sm">No motion alerts.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MotionAlertsScreen;
